import React, { useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { useNavigate, Link } from "react-router-dom";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

/** Canonical options (values used in code / DB after normalization) */
const FACILITY_MAP = {
  whatsapp: "WhatsApp",
  payments: "Payments",
  internal_chats: "Internal Chats",
  chatbots: "Chatbots",
  ai_scenarios: "AI Scenarios",
};

/** Normalize a single item from DB → canonical value key */
const normalizeFacility = (s) => {
  if (!s) return "";
  let v = String(s).trim();

  // strip quotes
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    v = v.slice(1, -1).trim();
  }

  v = v.toLowerCase().replace(/\s+/g, "_"); // spaces -> underscores

  // fix common variants/typos
  if (v === "internal_chat") v = "internal_chats";
  if (v === "ai_senerious" || v === "ai_scenarious" || v === "ai_scenario") v = "ai_scenarios";

  return FACILITY_MAP[v] ? v : ""; // return only if allowed
};

/** Parse facilities from API: array | JSON string | {a,b} | a,b */
const parseFacilities = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map(normalizeFacility).filter(Boolean);
  }
  if (raw == null) return [];

  const s = String(raw).trim();

  // JSON array string: ["a","b"]
  if (s.startsWith("[")) {
    try {
      const arr = JSON.parse(s);
      return Array.isArray(arr) ? arr.map(normalizeFacility).filter(Boolean) : [];
    } catch {
      // fall through
    }
  }

  // Postgres text[]: {"a","b with space"}
  if (s.startsWith("{") && s.endsWith("}")) {
    // prefer quoted items
    const quoted = [...s.matchAll(/"([^"]*)"/g)].map((m) => m[1]);
    if (quoted.length) {
      return quoted.map(normalizeFacility).filter(Boolean);
    }
    const inner = s.slice(1, -1);
    if (!inner) return [];
    return inner.split(",").map(normalizeFacility).filter(Boolean);
  }

  // Comma-separated: a,b
  return s.split(",").map(normalizeFacility).filter(Boolean);
};

/** Render facilities as labels joined by comma */
const renderFacilities = (raw) => {
  const keys = parseFacilities(raw);
  if (!keys.length) return "-";
  return keys.map((k) => FACILITY_MAP[k] || k).join(", ");
};

const Packageslist = () => {
  const navigate = useNavigate();
  const gridRef = useRef();

  const [rowData, setRowData] = useState([]);
  const [searchText, setSearchText] = useState("");

  // confirm dialog
  const [confirm, setConfirm] = useState({
    open: false,
    item: null,
    loading: false,
  });

  const columnDefs = [
    {
      field: "id",
      headerName: "Id",
      valueGetter: "node.rowIndex + 1",
      width: 90,
    },
    { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
    { field: "type", headerName: "Type", width: 130 }, // "package" | "add on"

    // Workspaces (flattened)
    {
      headerName: "Workspaces Mode",
      valueGetter: (p) => p.data?.workspace_mode ?? "-",
      width: 160,
    },
    {
      headerName: "Workspace Limit",
      valueGetter: (p) =>
        p.data?.workspace_mode === "limited"
          ? (p.data?.workspace_limit ?? "-")
          : "-",
      width: 160,
    },

    // Users
    {
      field: "users",
      headerName: "Users",
      width: 110,
      valueGetter: (p) =>
        typeof p.data?.users === "number" ? p.data.users : (p.data?.users ?? "-"),
    },
    {
      headerName: "Application Users",
      valueGetter: (p) =>
        typeof p.data?.application_users === "number"
          ? p.data.application_users
          : (p.data?.application_users ?? "-"),
      width: 170,
    },

    // Facilities — robust for any backend format
    {
      headerName: "Facilities",
      flex: 1,
      minWidth: 220,
      valueGetter: (p) => renderFacilities(p.data?.facilities),
    },

    // Price (flattened)
    {
      headerName: "Price",
      width: 150,
      valueGetter: (p) => {
        const amt = p.data?.price_amount;
        const cur = p.data?.price_currency || "";
        if (amt === null || amt === undefined || amt === "") return "-";
        return `${amt} ${cur}`;
      },
    },

    // Duration (flattened)
    {
      headerName: "Duration",
      width: 180,
      valueGetter: (p) => {
        const label = p.data?.duration_label || "-";
        const days = p.data?.duration_in_days;
        return days ? `${label} (${days}d)` : label;
      },
    },

    // Actions
    {
      headerName: "Action",
      width: 240,
      cellRenderer: (params) => (
        <div className="flex items-center">
          <Link to={`/packagesView/${params.data.id}`}>
            <button
              className="py-1 px-2 mr-2 rounded bg-blue-500 text-xs hover:bg-blue-600 text-white"
              title="View"
            >
              {/* Eye icon */}
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
            </button>
          </Link>

          <Link to={`/packagesEdit/${params.data.id}`}>
            <button
              className="py-1 px-2 mr-2 rounded bg-green-500 text-xs hover:bg-green-600 text-white"
              title="Edit"
            >
              {/* Pencil icon */}
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
              </svg>
            </button>
          </Link>

          <button
            className="py-1 px-2 rounded bg-red-500 text-xs hover:bg-red-600 text-white"
            onClick={() => openConfirm(params.data)}
            title="Delete"
          >
            {/* Trash icon */}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    sortable: true,
    resizable: true,
    filter: true,
    flex: 1,
    minWidth: 100,
  };

  useEffect(() => {
    const isAuth = JSON.parse(localStorage.getItem("access_token"));
    if (!isAuth) {
      navigate("/");
      return;
    }
    getData();
  }, [navigate]);

  const getData = async () => {
    fetch(`${process.env.REACT_APP_API_URL}/packages/all`, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        authorization:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
      }),
    })
      .then((r) => r.json())
      .then((resp) => {
        setRowData(Array.isArray(resp?.data) ? resp.data : []);
      })
      .catch(() => setRowData([]));
  };

  // search handlers (AG Grid quick filter)
  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (gridRef.current?.api) {
      gridRef.current.api.setQuickFilter(val);
    }
  };
  const clearSearch = () => {
    setSearchText("");
    if (gridRef.current?.api) {
      gridRef.current.api.setQuickFilter("");
    }
  };

  // confirm modal helpers
  const openConfirm = (item) => setConfirm({ open: true, item, loading: false });
  const closeConfirm = () => setConfirm({ open: false, item: null, loading: false });

  // delete with modal
  const doDelete = () => {
    if (!confirm?.item?.id) return;
    setConfirm((c) => ({ ...c, loading: true }));

    fetch(`${process.env.REACT_APP_API_URL}/packages/delete/${confirm.item.id}`, {
      method: "DELETE",
      headers: new Headers({
        "Content-Type": "application/json",
        authorization:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
      }),
    })
      .then((r) => r.json())
      .then(() => {
        closeConfirm();
        getData();
      })
      .catch(() => setConfirm((c) => ({ ...c, loading: false })));
  };

  // close modal on Esc / confirm with Enter
  useEffect(() => {
    if (!confirm.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeConfirm();
      if (e.key === "Enter") doDelete();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm.open]);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col items-start justify-between pb-6 space-y-4 lg:items-center lg:space-y-0 lg:flex-row">
        <h1 className="text-2xl font-semibold whitespace-nowrap">Packages</h1>

        <div className="flex items-center gap-3">
          {/* Search (right aligned) */}
          <div className="relative w-64">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {/* search icon */}
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="11" cy="11" r="8" strokeWidth="2" />
                <path d="M21 21l-4.35-4.35" strokeWidth="2" />
              </svg>
            </span>
            <input
              type="text"
              value={searchText}
              onChange={onSearchChange}
              placeholder="Search packages…"
              className="w-full rounded-md border border-gray-300 pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchText ? (
              <button
                type="button"
                title="Clear"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {/* X icon */}
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            ) : null}
          </div>

          <Link
            to="/packagesAdd"
            className="inline-flex items-center justify-center px-3 py-2 text-sm bg-green-500 text-white rounded-md shadow hover:bg-green-600"
            title="Add"
          >
            {/* plus icon */}
            <svg
              className="h-4 w-4 mr-1"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
            </svg>
            Add Package
          </Link>
        </div>
      </div>

      {/* Grid */}
      <div className="border p-1 mt-3 border-grey-200 rounded-md shadow-lg">
        <div className="ag-theme-alpine overflow-hidden" style={{ height: 500, marginTop: 8 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={10}
          />
        </div>
      </div>

      {/* Confirm Delete Modal */}
      {confirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeConfirm} />
          {/* Card */}
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-black/5">
            {/* Header */}
            <div className="px-6 pt-6">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                  {/* trash in circle */}
                  <svg
                    className="h-5 w-5 text-red-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M19 7l-1 12a2 2 0 01-2 2H8a2 2 0 01-2-2L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete package?</h3>
              </div>
              <p className="mt-3 text-sm text-gray-600">
                You are about to delete{" "}
                <span className="font-medium text-gray-900">
                  “{confirm.item?.name || "this package"}”
                </span>
                . This action cannot be undone.
              </p>
            </div>

            {/* Footer */}
            <div className="mt-6 flex items-center justify-end gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={closeConfirm}
                className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={confirm.loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white ${
                  confirm.loading ? "bg-red-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {confirm.loading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Deleting…
                  </span>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Packageslist;
